import React, { useState, useEffect } from 'react';
import evidenceAPI from '../services/evidenceAPI';
import {
  Zap, Loader2, TrendingUp, AlertCircle
} from 'lucide-react';
import AdminSecurityHelpButton from '../components/Admin/AdminSecurityHelpButton';

type ChangeResult = {
  summary: { controls_affected: number; policies_to_update: number; evidence_needed: number; standards_improved: number };
  required_actions: { title: string; update: string; tags: string[]; risk: string }[];
  compliance_forecast: { standard: string; before: number; after: number; direction: string }[];
};

const EXAMPLES = [
  'Adding a public API endpoint',
  'Enable MFA for all users',
  'Change firewall rules / open port',
  'Upgrade TLS certificates',
  'Deploy new microservice',
];

const ChangeImpact: React.FC = () => {
  const [input, setInput] = useState('');
  const [result, setResult] = useState<ChangeResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [evidenceCount, setEvidenceCount] = useState(0);

  useEffect(() => {
    evidenceAPI.getEvidenceItems().then(items => setEvidenceCount(items.length)).catch(() => {});
  }, []);

  const handleAnalyze = async () => {
    if (!input.trim() || loading) return;
    setLoading(true);
    setResult(null);
    setError(null);
    try {
      const data = await evidenceAPI.aiChangeImpact(input);
      setResult(data);
    } catch {
      setError('Analysis failed. Please ensure the backend server is running.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6 flex items-center gap-3">
        <div className="w-10 h-10 bg-yellow-100 rounded-xl flex items-center justify-center">
          <Zap className="w-5 h-5 text-yellow-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Change Impact Analysis</h1>
          <p className="text-sm text-gray-500">Simulate infrastructure changes and forecast compliance impact</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <div className="flex items-center gap-2 bg-yellow-50 border border-yellow-100 rounded-lg px-3 py-1.5">
            <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            <span className="text-xs text-yellow-700 font-medium">{evidenceCount} evidence items</span>
          </div>
          <AdminSecurityHelpButton screenId="change-impact" />
        </div>
      </div>

      {/* Input */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">Describe your planned change</label>
        <div className="flex gap-3">
          <input
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAnalyze()}
            placeholder="e.g. Adding a public API endpoint for mobile app..."
            className="flex-1 border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
          />
          <button
            onClick={handleAnalyze}
            disabled={loading || !input.trim()}
            className="flex items-center gap-2 px-6 py-3 bg-yellow-500 text-white rounded-xl hover:bg-yellow-600 disabled:opacity-40 transition-colors font-medium text-sm"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
            Analyze
          </button>
        </div>
        {/* Quick examples */}
        <div className="flex flex-wrap gap-2 mt-3">
          {EXAMPLES.map(ex => (
            <button
              key={ex}
              onClick={() => setInput(ex)}
              className="text-xs px-3 py-1.5 bg-gray-100 hover:bg-yellow-50 hover:text-yellow-700 text-gray-600 rounded-full border border-gray-200 transition-colors"
            >
              {ex}
            </button>
          ))}
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Loader2 className="w-8 h-8 animate-spin text-yellow-500 mx-auto mb-3" />
          <p className="text-sm text-gray-500">Analyzing compliance impact against {evidenceCount} evidence items...</p>
        </div>
      )}

      {/* Results */}
      {result && (
        <div className="space-y-5">
          {/* Summary */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'Controls Affected', value: result.summary.controls_affected, color: 'amber' },
              { label: 'Policies to Update', value: result.summary.policies_to_update, color: 'blue' },
              { label: 'Evidence Needed', value: result.summary.evidence_needed, color: 'purple' },
              { label: 'Standards Improved', value: result.summary.standards_improved, color: 'green' },
            ].map(({ label, value, color }) => (
              <div key={label} className={`bg-${color}-50 border border-${color}-100 rounded-xl p-5`}>
                <p className={`text-xs font-medium text-${color}-600 mb-1`}>{label}</p>
                <p className={`text-4xl font-bold text-${color}-700`}>{value}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Required Actions */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-4">Required Actions</h3>
              <div className="space-y-3">
                {result.required_actions.map((action, i) => (
                  <div key={i} className="flex gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 mt-2 ${
                      action.risk === 'HIGH' ? 'bg-red-500' : action.risk === 'MEDIUM' ? 'bg-amber-500' : 'bg-green-500'
                    }`} />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-800">{action.title}</p>
                      <p className="text-xs text-gray-500 mt-0.5">Update: {action.update}</p>
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {action.tags.map(t => (
                          <span key={t} className="text-xs px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded">{t}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Compliance Forecast */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-4">Compliance Score Forecast</h3>
              <div className="space-y-4">
                {result.compliance_forecast.map((f, i) => (
                  <div key={i}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-sm text-gray-700">{f.standard}</span>
                      <span className={`text-sm font-bold flex items-center gap-1 ${
                        f.direction === 'up' ? 'text-green-600' : f.direction === 'down' ? 'text-red-600' : 'text-gray-600'
                      }`}>
                        <span className="text-base">{f.before}%</span>
                        <span className="mx-1">→</span>
                        <span className="text-base">{f.after}%</span>
                        {f.direction === 'up' && <TrendingUp className="w-3.5 h-3.5 ml-0.5" />}
                        {f.direction === 'down' && <AlertCircle className="w-3.5 h-3.5 ml-0.5" />}
                      </span>
                    </div>
                    <div className="relative h-2.5 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`absolute inset-y-0 left-0 rounded-full transition-all duration-700 ${
                          f.direction === 'up' ? 'bg-green-500' : f.direction === 'down' ? 'bg-red-400' : 'bg-blue-400'
                        }`}
                        style={{ width: `${f.after}%` }}
                      />
                    </div>
                    {f.direction !== 'neutral' && (
                      <p className={`text-xs mt-1 ${f.direction === 'up' ? 'text-green-600' : 'text-red-500'}`}>
                        {f.direction === 'up' ? `+${f.after - f.before}% with this change` : `-${f.before - f.after}% impact`}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* New Analysis button */}
          <div className="flex justify-end">
            <button
              onClick={() => { setResult(null); setInput(''); }}
              className="text-sm px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-600 transition-colors"
            >
              New Analysis
            </button>
          </div>
        </div>
      )}

      {/* Error state */}
      {error && !loading && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-5 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Empty state */}
      {!result && !loading && !error && (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Zap className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Describe a planned change to see its compliance impact</p>
          <p className="text-sm text-gray-400 mt-1">Analyzes against your {evidenceCount} evidence items</p>
        </div>
      )}
    </div>
  );
};

export default ChangeImpact;
